import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useI18n, useTranslate } from "@/lib/i18n";
import { useOptionalRestaurantNotifications } from "@/modules/notifications/NotificationsProvider";
import { usePrivateToast } from "@/shared/usePrivateToast";

import { freshServiceRequests, SERVICE_REQUEST_ALERTS } from "./serviceRequests";
import { useServiceRequests } from "./useServiceRequests";

/**
 * D132 — THE FLOOR HEARS A TABLE CALL.
 *
 * D120's pattern, for a diner's ask instead of a waiter's: mounted once for the
 * signed-in app, active for whoever works the floor (`POS_USE`), re-reading on
 * each committed floor revision (and on focus/reconnect) and ringing the
 * notification chime with a toast for each NEW ask —
 * never for asks already open when the screen loaded. The toast opens the
 * table's bill when the ask names one, else the table.
 */
export function ServiceRequestAlerts() {
  const asks = useServiceRequests();
  const notifications = useOptionalRestaurantNotifications();
  const chimeSound = notifications?.chimeSound;
  const startSoundReminder = notifications?.startSoundReminder;
  const toast = usePrivateToast();
  const t = useTranslate();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!asks.data) return;
    const fresh = freshServiceRequests(seen.current, asks.data);
    // Asks marked done drop out of `seen` with the list, which is harmless:
    // a done ask never comes back under the same id.
    seen.current = new Set(asks.data.map((ask) => ask.id));
    for (const ask of fresh) {
      chimeSound?.(`ask:${ask.id}`);
      toast.info(t(SERVICE_REQUEST_ALERTS[ask.kind], { table: ask.tableName }), undefined, {
        durationMs: null,
        onDismiss: startSoundReminder?.(),
        onClick: () =>
          navigate(
            ask.invoiceId
              ? `/${locale}/pos/invoices/${encodeURIComponent(ask.invoiceId)}`
              : `/${locale}/pos/tables/${encodeURIComponent(ask.tableId)}`,
          ),
      });
    }
  }, [asks.data, chimeSound, startSoundReminder, locale, navigate, t, toast]);

  return null;
}
